import React, { useState, useMemo } from 'react';
import type { BusinessEntry } from '../api/useNotificationMetrics';

interface BusinessBreakdownTableProps {
  businesses: BusinessEntry[];
  loading: boolean;
}

type SortField = 'business_name' | 'total_sent' | 'total_opened' | 'open_rate' | 'attributed_visits';
type SortDir = 'asc' | 'desc';

const columns: { key: SortField; label: string; align: string }[] = [
  { key: 'business_name', label: 'Business Name', align: 'text-left' },
  { key: 'total_sent', label: 'Sent', align: 'text-right' },
  { key: 'total_opened', label: 'Opened', align: 'text-right' },
  { key: 'open_rate', label: 'Open Rate', align: 'text-right' },
  { key: 'attributed_visits', label: 'Attr. Visits', align: 'text-right' },
];

function BusinessBreakdownTable({ businesses, loading }: BusinessBreakdownTableProps): React.ReactElement {
  const [sortField, setSortField] = useState<SortField>('total_sent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    const copy = [...businesses];
    copy.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
    });
    return copy;
  }, [businesses, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="h-5 w-48 bg-gray-800 rounded animate-pulse mb-4" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-800/40 rounded animate-pulse mb-2" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-800">
        <h3 className="text-sm font-semibold text-white tracking-tight">Per-Business Breakdown</h3>
      </div>
      {businesses.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-gray-500">No business data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300 transition-colors select-none ${col.align}`}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sortField === col.key && (
                        <svg
                          className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((biz, i) => (
                <tr
                  key={biz.business_id}
                  className={`${i < sorted.length - 1 ? 'border-b border-gray-800/60' : ''} hover:bg-gray-800/20 transition-colors`}
                >
                  <td className="px-4 py-3.5 text-gray-200 font-medium">{biz.business_name}</td>
                  <td className="px-4 py-3.5 text-right text-gray-300">{biz.total_sent.toLocaleString()}</td>
                  <td className="px-4 py-3.5 text-right text-gray-300">{biz.total_opened.toLocaleString()}</td>
                  <td className="px-4 py-3.5 text-right text-gray-300">{biz.open_rate}%</td>
                  <td className="px-4 py-3.5 text-right text-gray-300">{biz.attributed_visits.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BusinessBreakdownTable;
